import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { ShieldCheck, Loader2, Download } from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * Super-admin only. Every rider account with the timestamp + IP they accepted
 * the T&Cs — one screen you can hand to a lawyer if a dispute ever arises.
 */
export default function ConsentLedgerTab() {
  const [data, setData] = useState({ rows: [], total: 0 });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/admin/consent-ledger");
        setData(data);
      } finally { setLoading(false); }
    })();
  }, []);

  const exportCsv = () => {
    const header = ["email", "name", "mobile", "terms_accepted_at", "terms_accepted_ip", "created_at"];
    const rows = [header.join(",")].concat(
      data.rows.map((r) => header.map((k) => `"${(r[k] ?? "").toString().replace(/"/g, '""')}"`).join(","))
    );
    const blob = new Blob([rows.join("\n")], { type: "text/csv" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url; a.download = `consent-ledger-${new Date().toISOString().slice(0,10)}.csv`;
    a.click(); URL.revokeObjectURL(url);
  };

  return (
    <div data-testid="consent-ledger-tab">
      <div className="flex items-start justify-between mb-4">
        <div>
          <div className="text-caption">Legal</div>
          <h1 className="font-display text-3xl font-extrabold">Consent Ledger</h1>
          <p className="text-slate-400 text-sm mt-1">Proof of Terms &amp; Conditions acceptance for every rider account.</p>
        </div>
        <Button onClick={exportCsv} className="btn-gold rounded-full" data-testid="consent-export-csv">
          <Download className="w-3.5 h-3.5 mr-1.5" /> Export CSV
        </Button>
      </div>

      <div className="mb-4 inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-red-500/10 border border-red-400/30 text-xs font-mono uppercase tracking-wider text-red-300">
        <ShieldCheck className="w-3.5 h-3.5" /> {data.total} riders tracked
      </div>

      {loading ? (
        <div className="glass-card rounded-2xl p-8 flex items-center gap-2 text-slate-400"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
      ) : (
        <div className="glass-card gold-border rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-black/30 border-b border-white/5">
                <tr className="text-[10px] uppercase tracking-widest font-mono text-slate-400">
                  <th className="text-left px-4 py-3">Rider</th>
                  <th className="text-left px-4 py-3">Email</th>
                  <th className="text-left px-4 py-3">Accepted at</th>
                  <th className="text-left px-4 py-3">IP</th>
                </tr>
              </thead>
              <tbody>
                {data.rows.length === 0 ? (
                  <tr><td colSpan={4} className="text-center py-8 text-slate-500">No rider accounts yet.</td></tr>
                ) : data.rows.map((r) => (
                  <tr key={r.id} className="border-b border-white/5 hover:bg-black/20" data-testid={`consent-row-${r.id}`}>
                    <td className="px-4 py-3">
                      <div className="text-white">{r.name || "—"}</div>
                      <div className="text-[11px] text-slate-500 font-mono">{r.mobile || ""}</div>
                    </td>
                    <td className="px-4 py-3 font-mono text-slate-300">{r.email}</td>
                    <td className="px-4 py-3 font-mono text-xs">
                      {r.terms_accepted_at
                        ? <span className="text-emerald-300">{new Date(r.terms_accepted_at).toLocaleString()}</span>
                        : <span className="text-amber-400">legacy — no record</span>}
                    </td>
                    <td className="px-4 py-3 font-mono text-xs text-slate-400">{r.terms_accepted_ip || "—"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
